import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { ArrowLeft, Upload, Trash2, Search, Image as ImageIcon, Copy, Check, FolderOpen, X, Grid, List as ListIcon, CheckSquare, Square, Video, Play } from 'lucide-react';
import axios from 'axios';
import { ImgbbMigrationBanner } from '../../components/admin/ImgbbMigrationBanner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to check if file is video
const isVideoFile = (item) => {
  if (item.media_type === 'video') return true;
  if (item.content_type?.startsWith('video/')) return true;
  const ext = item.url?.split('.').pop()?.toLowerCase();
  return ['mp4', 'webm', 'mov', 'avi', 'mpeg'].includes(ext);
};

const AdminImageLibrary = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('todas');
  const [categories, setCategories] = useState(['geral', 'pacotes', 'destinos', 'banners', 'videos']);
  const [copiedId, setCopiedId] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('geral');
  const [dragOver, setDragOver] = useState(false);
  const [mediaFilter, setMediaFilter] = useState('todos'); // 'todos', 'imagens', 'videos'
  
  // Multi-select state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadImages();
    loadCategories();
  }, [navigate]);

  const loadImages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/images/`);
      setImages(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar mídia:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/images/categories`);
      if (response.data && response.data.length > 0) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    await uploadFiles(files);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    await uploadFiles(files);
  };

  const uploadFiles = async (files) => {
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', uploadCategory);
        formData.append('alt_text', file.name.replace(/\.[^/.]+$/, ''));

        await axios.post(`${API_URL}/images/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      await loadImages();
      alert(`${files.length} imagem(ns) enviada(s) com sucesso!`);
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      alert('Erro ao fazer upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta imagem?')) return;

    try {
      await axios.delete(`${API_URL}/images/${imageId}`);
      setImages(images.filter(img => img.id !== imageId));
      if (selectedImage?.id === imageId) setSelectedImage(null);
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert('Erro ao excluir imagem');
    }
  };

  // Multi-select functions
  const toggleImageSelection = (imageId) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(imageId)) {
      newSelected.delete(imageId);
    } else {
      newSelected.add(imageId);
    }
    setSelectedImages(newSelected);
  };

  const selectAllVisible = () => {
    const allIds = new Set(filteredImages.map(img => img.id));
    setSelectedImages(allIds);
  };

  const deselectAll = () => {
    setSelectedImages(new Set());
  };

  const handleDeleteSelected = async () => {
    if (selectedImages.size === 0) return;
    
    if (!window.confirm(`Tem certeza que deseja excluir ${selectedImages.size} imagem(ns)?`)) return;

    setDeleting(true);
    try {
      let deletedCount = 0;
      for (const imageId of selectedImages) {
        try {
          await axios.delete(`${API_URL}/images/${imageId}`);
          deletedCount++;
        } catch (error) {
          console.error(`Erro ao excluir imagem ${imageId}:`, error);
        }
      }
      
      setImages(images.filter(img => !selectedImages.has(img.id)));
      setSelectedImages(new Set());
      setSelectionMode(false);
      alert(`${deletedCount} imagem(ns) excluída(s) com sucesso!`);
    } catch (error) {
      console.error('Erro ao excluir imagens:', error);
      alert('Erro ao excluir algumas imagens');
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      setSelectedImages(new Set());
    }
  };

  const copyToClipboard = (url) => {
    const fullUrl = url?.startsWith('/') ? `${window.location.origin}${url}` : url;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(url);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredImages = images.filter(img => {
    const matchesSearch = !searchTerm || 
      img.original_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      img.alt_text?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'todas' || img.category === selectedCategory;
    const matchesMediaType = mediaFilter === 'todos' || 
      (mediaFilter === 'videos' && isVideoFile(img)) ||
      (mediaFilter === 'imagens' && !isVideoFile(img));
    return matchesSearch && matchesCategory && matchesMediaType;
  });

  // Count media types
  const videoCount = images.filter(img => isVideoFile(img)).length;
  const imageCount = images.length - videoCount;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="hover:text-[#FFD700]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div className="flex items-center space-x-2">
              <ImageIcon className="w-6 h-6" />
              <h1 className="text-xl font-bold">Biblioteca de Mídia</h1>
            </div>
          </div>
          <div className="text-sm text-gray-300 flex items-center gap-4">
            <span className="flex items-center gap-1"><ImageIcon className="w-4 h-4" /> {imageCount} imagens</span>
            <span className="flex items-center gap-1"><Video className="w-4 h-4" /> {videoCount} vídeos</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <ImgbbMigrationBanner onFinished={loadImages} />
        {/* Upload Area */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <h2 className="text-lg font-bold text-gray-900 flex items-center">
                <Upload className="w-5 h-5 mr-2 text-[#003366]" />
                Upload de Imagens e Vídeos
              </h2>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Categoria:</Label>
                <Select value={uploadCategory} onValueChange={setUploadCategory}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                dragOver ? 'border-[#FFD700] bg-yellow-50' : 'border-gray-300 hover:border-[#003366]'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <div className="flex justify-center gap-4 mb-4">
                <ImageIcon className="w-10 h-10 text-gray-400" />
                <Video className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-2">Arraste e solte imagens ou vídeos aqui ou</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-[#003366] hover:bg-[#002244] text-white"
              >
                {uploading ? 'Enviando...' : 'Selecionar Arquivos'}
              </Button>
              <p className="text-xs text-gray-500 mt-2">Imagens: JPG, PNG, WebP, GIF (máx. 10MB) | Vídeos: MP4, WebM, MOV (máx. 100MB)</p>
            </div>
          </CardContent>
        </Card>

        {/* Filters and Selection Mode */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar imagens e vídeos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={mediaFilter} onValueChange={setMediaFilter}>
                <SelectTrigger className="w-full md:w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="imagens">Só Imagens</SelectItem>
                  <SelectItem value="videos">Só Vídeos</SelectItem>
                </SelectContent>
              </Select>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-48">
                  <FolderOpen className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as Categorias</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setViewMode('grid')}
                  className={viewMode === 'grid' ? 'bg-[#003366]' : ''}
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' ? 'bg-[#003366]' : ''}
                >
                  <ListIcon className="w-4 h-4" />
                </Button>
              </div>
              <Button
                variant={selectionMode ? 'default' : 'outline'}
                onClick={toggleSelectionMode}
                className={selectionMode ? 'bg-orange-500 hover:bg-orange-600' : ''}
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                {selectionMode ? 'Cancelar Seleção' : 'Selecionar'}
              </Button>
            </div>

            {/* Selection Actions Bar */}
            {selectionMode && (
              <div className="mt-4 flex flex-wrap items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                <span className="text-sm font-medium text-orange-800">
                  {selectedImages.size} imagem(ns) selecionada(s)
                </span>
                <div className="flex gap-2 ml-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllVisible}
                    className="text-xs"
                  >
                    Selecionar Todas ({filteredImages.length})
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={deselectAll}
                    className="text-xs"
                  >
                    Limpar Seleção
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDeleteSelected}
                    disabled={selectedImages.size === 0 || deleting}
                    className="text-xs"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    {deleting ? 'Excluindo...' : `Excluir (${selectedImages.size})`}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Images Grid/List */}
        {filteredImages.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="flex justify-center gap-4 mb-4">
                <ImageIcon className="w-12 h-12 text-gray-300" />
                <Video className="w-12 h-12 text-gray-300" />
              </div>
              <p className="text-gray-500">Nenhuma mídia encontrada</p>
              <p className="text-sm text-gray-400 mt-2">Faça upload de imagens ou vídeos para começar</p>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredImages.map((image) => {
              const isSelected = selectedImages.has(image.id);
              const isVideo = isVideoFile(image);
              return (
                <Card 
                  key={image.id} 
                  className={`overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group ${
                    isSelected ? 'ring-2 ring-orange-500 ring-offset-2' : ''
                  }`}
                >
                  <div 
                    className="relative aspect-square bg-gray-100" 
                    onClick={() => selectionMode ? toggleImageSelection(image.id) : setSelectedImage(image)}
                  >
                    {isVideo ? (
                      <video
                        src={image.url}
                        className="w-full h-full object-cover"
                        muted
                        onMouseEnter={(e) => e.target.play()}
                        onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                      />
                    ) : (
                      <img
                        src={image.url}
                        alt={image.alt_text || image.original_name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f0f0f0" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">Erro</text></svg>';
                        }}
                      />
                    )}
                    
                    {/* Video indicator */}
                    {isVideo && (
                      <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs">
                        <Play className="w-3 h-3" fill="white" />
                        Vídeo
                      </div>
                    )}
                    
                    {/* Selection checkbox */}
                    {selectionMode && (
                      <div className="absolute top-2 left-2 z-10">
                        <div 
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                            isSelected 
                              ? 'bg-orange-500 border-orange-500' 
                              : 'bg-white/80 border-gray-400 hover:border-orange-500'
                          }`}
                        >
                          {isSelected && <Check className="w-4 h-4 text-white" />}
                        </div>
                      </div>
                    )}
                    
                    {/* Hover overlay - only when not in selection mode */}
                    {!selectionMode && (
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex gap-2">
                          <Button
                            size="icon"
                            variant="secondary"
                            onClick={(e) => { e.stopPropagation(); copyToClipboard(image.url); }}
                            title="Copiar URL"
                          >
                            {copiedId === image.url ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="icon"
                            variant="destructive"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(image.id); }}
                            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(image.id); }}
                            title="Excluir"
                            className="min-w-[40px] min-h-[40px]"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Selection overlay */}
                    {selectionMode && isSelected && (
                      <div className="absolute inset-0 bg-orange-500/20 pointer-events-none" />
                    )}
                  </div>
                  <CardContent className="p-3">
                    <p className="text-xs font-medium truncate text-gray-800">{image.original_name}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-500">{formatFileSize(image.size)}</span>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600">{image.category}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {selectionMode && (
                      <th className="px-4 py-3 text-left">
                        <Checkbox
                          checked={selectedImages.size === filteredImages.length && filteredImages.length > 0}
                          onCheckedChange={(checked) => checked ? selectAllVisible() : deselectAll()}
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Imagem</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tamanho</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    {!selectionMode && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredImages.map((image) => {
                    const isSelected = selectedImages.has(image.id);
                    return (
                      <tr 
                        key={image.id} 
                        className={`hover:bg-gray-50 ${isSelected ? 'bg-orange-50' : ''}`}
                        onClick={() => selectionMode && toggleImageSelection(image.id)}
                      >
                        {selectionMode && (
                          <td className="px-4 py-3">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleImageSelection(image.id)}
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <img src={image.url} alt="" className="w-12 h-12 object-cover rounded" />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-800">{image.original_name}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-1 bg-gray-100 rounded">{image.category}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatFileSize(image.size)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(image.created_at)}</td>
                        {!selectionMode && (
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="icon"
                                variant="outline"
                                onClick={() => copyToClipboard(image.url)}
                              >
                                {copiedId === image.url ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                              </Button>
                              <Button
                                size="icon"
                                variant="outline"
                                className="text-red-600 hover:text-red-700 min-w-[40px] min-h-[40px]"
                                onClick={(e) => { e.preventDefault(); handleDelete(image.id); }}
                                onTouchEnd={(e) => { e.preventDefault(); handleDelete(image.id); }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Floating delete button when in selection mode with items selected */}
        {selectionMode && selectedImages.size > 0 && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
            <Button
              variant="destructive"
              size="lg"
              onClick={handleDeleteSelected}
              disabled={deleting}
              className="shadow-lg px-6"
            >
              <Trash2 className="w-5 h-5 mr-2" />
              {deleting ? 'Excluindo...' : `Excluir ${selectedImages.size} imagem(ns)`}
            </Button>
          </div>
        )}

        {/* Media Preview Modal */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
            <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <Button
                size="icon"
                variant="secondary"
                className="absolute -top-12 right-0"
                onClick={() => setSelectedImage(null)}
              >
                <X className="w-5 h-5" />
              </Button>
              {isVideoFile(selectedImage) ? (
                <video
                  src={selectedImage.url}
                  controls
                  autoPlay
                  className="w-full max-h-[70vh] object-contain rounded-lg bg-black"
                />
              ) : (
                <img
                  src={selectedImage.url}
                  alt={selectedImage.alt_text || selectedImage.original_name}
                  className="w-full max-h-[70vh] object-contain rounded-lg"
                />
              )}
              <div className="mt-4 bg-white rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  {isVideoFile(selectedImage) ? (
                    <Video className="w-5 h-5 text-blue-600" />
                  ) : (
                    <ImageIcon className="w-5 h-5 text-green-600" />
                  )}
                  <p className="font-medium text-gray-800">{selectedImage.original_name}</p>
                </div>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                  <span>Categoria: {selectedImage.category}</span>
                  <span>Tamanho: {formatFileSize(selectedImage.size)}</span>
                  <span>Tipo: {isVideoFile(selectedImage) ? 'Vídeo' : 'Imagem'}</span>
                </div>
                <div className="mt-3 flex gap-2">
                  <Input value={`${window.location.origin}${selectedImage.url}`} readOnly className="flex-1" />
                  <Button onClick={() => copyToClipboard(selectedImage.url)} className="bg-[#003366]">
                    {copiedId === selectedImage.url ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {copiedId === selectedImage.url ? 'Copiado!' : 'Copiar URL'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminImageLibrary;
